import { useState, type ReactNode } from 'react'

import { signIn } from '@/shared/services/session/compat'
import { useIsClient } from 'usehooks-ts'

import { useAuthContext } from '@/features/auth/model/auth-context'
import { cn } from '@/lib/utils'
import { AppIcon } from '@/shared/ui/icons'

import { SocialIcon } from './SocialIcon'

/**
 * Provedores de autenticação em linhas.
 *
 * Componente único das duas rotas. Antes, `/auth/sign-in` usava estas linhas e
 * `/auth/sign-up` usava botões `variant="light"` com outro desenho — o mesmo
 * conceito com duas aparências, o que fazia as telas parecerem de produtos
 * diferentes ao trocar de rota.
 *
 * O verbo vem por prop porque é a única coisa que legitimamente muda entre
 * entrar e cadastrar.
 */

type ProviderRowsProps = {
  /** Verbo do rótulo: "Entrar" no acesso, "Cadastrar" no cadastro. */
  verb?: string
  /** Quando presente, acrescenta a linha de e-mail ao fim da lista. */
  onEmailSelect?: () => void
  emailLabel?: string
}

const SOCIAL_ROWS = [
  { id: 'google', name: 'Google', icon: <SocialIcon name="google" /> },
  { id: 'apple', name: 'Apple', icon: <SocialIcon name="apple" /> }
] as const

function ProviderRow({
  icon,
  label,
  onClick,
  loading = false,
  className
}: {
  icon: ReactNode
  label: string
  onClick: () => void
  /** Enquanto o provedor externo carrega, a linha assume o estado ocupado. */
  loading?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-busy={loading || undefined}
      className={cn(
        'grid min-h-[52px] w-full grid-cols-[40px_1fr_20px] items-center gap-2.5 short:min-h-12 short:py-2',
        'rounded-[var(--control-r-md)] bg-white px-3 py-2.5 text-left',
        // `border-contour` é o token de aresta de bloco apoiado no fundo
        // (MASTER §1). Mais claro que `border-input`, e aqui a delimitação não
        // depende só dele: a sombra também separa a linha da página.
        'border border-contour shadow-sm transition-colors',
        'hover:bg-gray-100 active:bg-gray-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
        // Sem isto, o redirecionamento para o provedor — que demora — deixava a
        // linha com aparência de clicável e sem nenhum sinal de que já respondeu.
        'disabled:cursor-wait disabled:bg-gray-100',
        className
      )}
    >
      <span className="flex items-center justify-center">{icon}</span>
      {/* Alinhado à esquerda: centralizado, o rótulo flutuava longe do ícone e
          as três linhas não compartilhavam um eixo de leitura. */}
      <span className="text-left text-sm font-semibold text-gray-900">
        {label}
      </span>
      {loading ? (
        <AppIcon
          name="spinner-third"
          size={16}
          className="animate-spin text-brand"
          aria-hidden
        />
      ) : (
        <AppIcon name="arrow-right" size={16} className="text-gray-900" />
      )}
    </button>
  )
}

export function AuthProviderRows({
  verb = 'Entrar',
  onEmailSelect,
  emailLabel
}: ProviderRowsProps) {
  const { providers, callbackUrl } = useAuthContext()
  const isClient = useIsClient()
  // Guarda qual provedor foi acionado: o `signIn` sai da página, e até o
  // navegador trocar de contexto passam alguns segundos sem retorno visual.
  const [carregando, setCarregando] = useState<string | null>(null)

  // A lista real de provedores só existe depois da hidratação. Até lá as linhas
  // são renderizadas mesmo assim, para a área não saltar quando ela chega.
  const disponiveis = isClient ? providers : {}

  const handleSocialSignIn = (providerId: string) => {
    if (!Object.prototype.hasOwnProperty.call(disponiveis, providerId)) return
    setCarregando(providerId)
    void signIn(providerId, { callbackUrl })
  }

  return (
    <div className="flex flex-col gap-2.5 short:gap-2">
      {SOCIAL_ROWS.map(row => (
        <ProviderRow
          key={row.id}
          icon={row.icon}
          label={`${verb} com ${row.name}`}
          loading={carregando === row.id}
          onClick={() => handleSocialSignIn(row.id)}
        />
      ))}

      {onEmailSelect ? (
        <ProviderRow
          icon={<SocialIcon name="email" />}
          label={emailLabel ?? `${verb} com e-mail`}
          onClick={onEmailSelect}
        />
      ) : null}
    </div>
  )
}
